import { useEffect, useState } from 'react';
import { HelpCircle, X } from 'lucide-react';

import SectionKicker from '@/components/landing/v3/SectionKicker';
import useReducedMotion from '@/hooks/useReducedMotion';
import {
  COLORES_LEYENDA,
  MIA,
  RIVALES,
  balanceCon,
  mediaRejilla,
} from '@/components/landing/v3/mapaSeoDatos';
import {
  deltaColor, deltaLabel, legendBuckets, rankColor, rankLabel,
} from '@/lib/geogridScale';
import FondoMapa from './FondoMapa';

/**
 * MapaSeoComparador — la sección que enseña la cuadrícula de posiciones y la
 * compara con la de un competidor, más el modal-guía de dos pasos que explica
 * qué se está mirando.
 *
 * La cuadrícula cicla sola entre tres vistas (mi negocio → competidor →
 * diferencia) y cambia de rival cada vuelta completa. Con
 * `prefers-reduced-motion` se queda quieta en la vista de DIFERENCIA, que es la
 * que cuenta la historia entera; parada en «mi negocio» no se entendería para
 * qué está la lista de competidores de al lado.
 *
 * La lista de la izquierda es interactiva: pulsar un competidor lo selecciona y
 * **para el ciclo**. Quien está comparando a mano no quiere que la vista se le
 * mueva debajo. En el diseño los radios eran decorativos y no se podían pulsar.
 */
const CICLO_MS = 2800;
const VISTAS = ['mio', 'rival', 'diff'];

export default function MapaSeoComparador({ c }) {
  const quieto = useReducedMotion();

  const [vista, setVista] = useState(quieto ? 2 : 0);
  const [rival, setRival] = useState(0);
  // Al elegir un rival a mano el ciclo se detiene y ya no vuelve solo.
  const [manual, setManual] = useState(false);
  const [guia, setGuia] = useState(0);

  useEffect(() => {
    if (quieto || manual) return undefined;

    const t0 = Date.now();
    const id = setInterval(() => {
      const paso = Math.floor((Date.now() - t0) / CICLO_MS);
      setVista(paso % 3);
      setRival(Math.floor(paso / 3) % RIVALES.length);
    }, 200);

    return () => clearInterval(id);
  }, [quieto, manual]);

  const elegido = RIVALES[rival];
  const balance = balanceCon(elegido.rejilla);
  const clave = VISTAS[vista];

  const nombreRival = `${c.competidor} ${rival + 1}`;
  const titulo =
    clave === 'rival' ? nombreRival : clave === 'diff' ? `${c.tu} vs ${nombreRival}` : c.tuNegocio;
  // Se calcula SIEMPRE, no solo en la vista de diferencia: `PieTarjeta` pinta
  // las dos variantes a la vez para reservar la altura (ver su cabecera).
  const resumenDiff = c.resumenDiff
    .replace('{gano}', balance.gano)
    .replace('{pierdo}', balance.pierdo)
    .replace('{empate}', balance.empate);

  return (
    <section id="mapa-seo" className="max-w-[1180px] mx-auto px-6 pt-16 md:pt-[76px]">
      <SectionKicker>{c.kicker}</SectionKicker>

      <h2 className="text-[26px] md:text-[32px] leading-[1.15] tracking-[-.025em] font-bold mt-6 max-w-[26ch] text-v3-ink">
        {c.titulo}
      </h2>
      <p className="text-[16.5px] leading-[1.65] text-v3-body mt-3.5 max-w-[62ch]">{c.cuerpo}</p>

      <button
        type="button"
        onClick={() => setGuia(1)}
        className="mt-5 inline-flex items-center gap-2.5 bg-v3-gold hover:bg-v3-golddeep text-v3-ink text-sm font-semibold rounded-full px-5 py-3 transition-colors"
      >
        <HelpCircle className="w-4 h-4" aria-hidden="true" />
        {c.abrirGuia}
      </button>

      <div className="grid gap-8 mt-6 md:grid-cols-2 items-start">
        <ListaRivales
          c={c}
          seleccionado={rival}
          onElegir={(i) => {
            setRival(i);
            setManual(true);
            setVista(2);
          }}
        />

        <div className="bg-white border border-v3-line rounded-[20px] v3-shadow-lg p-4 max-w-[420px] w-full mx-auto">
          <div className="flex items-center justify-between gap-2.5 px-1 pb-3.5">
            <span className="text-[13.5px] font-bold tracking-[-.01em] truncate text-v3-ink">
              {titulo}
            </span>
            <EtiquetaVista clave={clave} c={c} />
          </div>

          <Cuadricula clave={clave} rejilla={elegido.rejilla} globo={c.globoCentro} />

          <PieTarjeta clave={clave} c={c} resumenDiff={resumenDiff} />
        </div>
      </div>

      <ModalGuia paso={guia} onPaso={setGuia} c={c.guia} />
    </section>
  );
}

/**
 * PieTarjeta — la leyenda de color y el resumen, con la altura RESERVADA.
 *
 * ── POR QUÉ NO ES UN `min-height` ───────────────────────────────────────────
 *
 * Las dos piezas cambian de tamaño con la vista: la leyenda pasa de siete
 * tramos (escala secuencial, dos líneas) a cuatro (divergente, una línea), y el
 * resumen de la diferencia es más corto que el de base. Medido: la tarjeta
 * encogía de 569 a 543 px cada vez que el ciclo entraba en «Diferencia». Esta
 * sección está a media página, así que los 26 px se los comía todo lo de
 * debajo de golpe — que es lo que se lee como un scroll repentino.
 *
 * Se arregla apilando las DOS variantes en la misma celda de rejilla y tapando
 * la que no toca con `invisible`: sigue ocupando su sitio, así que la altura es
 * siempre la de la más alta y la mide el navegador. Un `min-height` en píxeles
 * —que es lo que había en el resumen, y por tres píxeles no llegaba— vale para
 * el copy de hoy en un idioma y se rompe en el primero que alargue
 * «Posición 1-3».
 *
 * La capa tapada va además `aria-hidden`: `visibility:hidden` ya la saca del
 * árbol de accesibilidad y de la selección de texto, pero dejarlo escrito
 * evita que alguien lo cambie a `opacity-0` sin darse cuenta de lo que pierde.
 */
function PieTarjeta({ clave, c, resumenDiff }) {
  const esDiff = clave === 'diff';

  return (
    <div className="grid">
      <CapaPie oculta={esDiff}>
        {/* Las etiquetas de los tramos viven en el copy de la guía (`c.guia`),
            que es donde se explican; aquí se reutilizan para no tener dos
            listas que se puedan separar. */}
        <LeyendaEscala
          tramos={legendBuckets().map((tramo, i) => ({
            color: tramo.color,
            texto: c.guia.leyenda[i],
          }))}
        />
        <ResumenPie texto={c.resumenBase} />
      </CapaPie>

      <CapaPie oculta={!esDiff}>
        <LeyendaEscala
          tramos={[
            { color: deltaColor(3), texto: c.leyendaGanas },
            { color: deltaColor(0), texto: c.leyendaEmpate },
            { color: deltaColor(-3), texto: c.leyendaPierdes },
            { color: deltaColor(null), texto: c.leyendaSinDato },
          ]}
        />
        <ResumenPie texto={resumenDiff} />
      </CapaPie>
    </div>
  );
}

/** Una de las dos variantes del pie. Las dos caen en la misma celda 1/1. */
function CapaPie({ oculta, children }) {
  return (
    <div
      className={`[grid-area:1/1] ${oculta ? 'invisible' : ''}`}
      aria-hidden={oculta || undefined}
    >
      {children}
    </div>
  );
}

function ResumenPie({ texto }) {
  return <p className="text-[13px] text-v3-body px-1 pt-3.5">{texto}</p>;
}

/**
 * La leyenda de color, que cambia con la vista —igual que `GeoGridLegend` en el
 * panel—.
 *
 * Es la pieza que faltaba para que los colores se entiendan: en «Mi negocio» y
 * «Competidor» la escala es SECUENCIAL (qué tal voy, del verde al granate), y en
 * «Diferencia» es DIVERGENTE (quién gana, con el empate en el centro). Son dos
 * preguntas distintas y el producto lo dice con dos paletas distintas; enseñar
 * solo una de las dos deja media pantalla sin explicar.
 *
 * Los colores no se escriben aquí: salen de `geogridScale`, el mismo módulo del
 * panel.
 */
function LeyendaEscala({ tramos }) {
  return (
    <ul className="flex flex-wrap gap-x-3.5 gap-y-1.5 px-1 pt-3">
      {tramos.map((tramo) => (
        <li key={tramo.texto} className="flex items-center gap-1.5 text-[11.5px] text-v3-muted">
          <span
            aria-hidden="true"
            className="w-2.5 h-2.5 rounded-full block shrink-0"
            style={{ background: tramo.color }}
          />
          {tramo.texto}
        </li>
      ))}
    </ul>
  );
}

function EtiquetaVista({ clave, c }) {
  const estilos = {
    mio: { bg: '#eaf6ee', fg: '#16a34a', texto: c.vistas.mio },
    rival: { bg: '#fdeaea', fg: '#dc2626', texto: c.vistas.rival },
    diff: { bg: '#fdf1dc', fg: '#e69a1e', texto: c.vistas.diff },
  }[clave];

  return (
    <span
      className="text-[11.5px] font-semibold rounded-full px-2.5 py-1.5 whitespace-nowrap transition-colors duration-200"
      style={{ background: estilos.bg, color: estilos.fg }}
    >
      {estilos.texto}
    </span>
  );
}

/**
 * La cuadrícula 5x5 sobre el callejero de mentira.
 *
 * Va `aria-hidden`: veinticinco números sueltos sin contexto no dicen nada a un
 * lector de pantalla, y el párrafo de resumen que hay debajo ya cuenta lo que
 * se ve.
 */
/** Índice del punto central en la rejilla 5×5. */
const CENTRO = 12;

/**
 * El globo que sale del punto central.
 *
 * Existe porque el centro dejó de distinguirse: la maqueta lo marcaba con un
 * pin negro que el panel no dibuja, se quitó, y entonces tu local pasó a ser
 * uno más de los veinticinco discos. Esto lo dice sin inventarse una interfaz
 * que no existe —es un adorno de la landing, no una captura del producto—.
 *
 * `bottom-full` lo apoya justo encima del disco sin saber cuánto mide, que
 * cambia con el ancho. La animación vive en `landingV3.css` (`.v3-globo`), con
 * su apagado por `prefers-reduced-motion`: ahí se queda puesto en vez de
 * desaparecer, porque quien pide menos movimiento no quiere perderse el texto.
 */
function GloboCentro({ texto }) {
  return (
    <span
      aria-hidden="true"
      className="v3-globo absolute bottom-full left-1/2 mb-2.5 whitespace-nowrap bg-v3-ink text-white text-[11px] font-semibold rounded-lg px-2.5 py-1.5 pointer-events-none"
    >
      {texto}
      {/* El pico, girado 45º y medio escondido bajo el globo. */}
      <span className="absolute left-1/2 top-full -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-v3-ink rotate-45" />
    </span>
  );
}

/**
 * La cuadrícula 5×5 sobre el plano.
 *
 * Se EXPORTA porque el hero de Visibilidad SEO enseña la misma pieza.
 *
 * ── USA LA ESCALA DEL PRODUCTO, NO UNA PROPIA ───────────────────────────────
 *
 * Los colores, las etiquetas y el borde salen de `lib/geogridScale.js`, que es
 * el mismo módulo que pinta los marcadores del panel (`GeoGridMap`). Antes esto
 * tenía su paleta aparte y se había separado en cuatro de los seis tramos: el
 * verde del top 3 era `#16a34a` y en el producto es `#059669`, el ámbar y los
 * rojos también bailaban. Una captura de la landing al lado del producto no
 * casaba, que es justo lo que no puede pasar aquí.
 *
 * DOS COSAS QUE LA MAQUETA SE HABÍA INVENTADO y no existen en el panel:
 *
 *   - **El pin negro del centro.** `GeoGridMap` dibuja un disco por punto y
 *     nada más; el centro es un punto medido como los otros veinticuatro, con
 *     su número. El círculo negro con el «◎» dentro no sale por ninguna parte.
 *   - **Las flechas del modo Diferencia.** El producto escribe la diferencia
 *     con su signo (`+3`, `-2`, `deltaLabel`), no ↑ y ↓.
 *
 * El aspecto del disco también es el del `Marker` real: aro blanco de 2,5 px,
 * relleno al 92 % —deja ver algo del mapa— y SIN sombra, porque un marcador de
 * Google no la lleva.
 */
export function Cuadricula({ clave, rejilla, globo }) {
  const celdas = MIA.map((mio, i) => {
    const suyo = rejilla[i];
    const mioReal = mio > 0 ? mio : null;
    const suyoReal = suyo > 0 ? suyo : null;

    if (clave === 'mio') return { n: rankLabel(mioReal), bg: rankColor(mioReal) };
    if (clave === 'rival') return { n: rankLabel(suyoReal), bg: rankColor(suyoReal) };

    // Diferencia: la del panel es la del rival MENOS la nuestra, y positiva
    // significa que le ganamos.
    const delta = (mioReal == null || suyoReal == null) ? null : suyoReal - mioReal;
    return { n: deltaLabel(delta), bg: deltaColor(delta) };
  });

  return (
    <div
      aria-hidden="true"
      className="relative rounded-2xl overflow-hidden bg-[#eef0ea] aspect-square flex items-center justify-center"
    >
      <FondoMapa className="absolute inset-0 w-full h-full pointer-events-none" />

      {/* Discos más pequeños y más separados de lo que pedía el diseño.
          Ocupaban el 82 % del cuadro y casi se tocaban, y así el plano de
          debajo no se veía: la rejilla parecía flotar sobre un fondo de color
          en vez de estar PUESTA sobre un mapa. En el panel se ve mucho mapa
          entre punto y punto. */}
      <div className="relative grid grid-cols-5 gap-2.5 md:gap-4 w-[74%]">
        {celdas.map((celda, i) => (
          <div
            key={i}
            className="relative aspect-square rounded-full flex items-center justify-center text-[11px] md:text-[13px] font-bold text-white transition-colors duration-200"
            /* El 92 % va en el COLOR y no en `opacity`: el globo cuelga de
               la celda central y heredaba la transparencia del disco. */
            style={{
              background: `${celda.bg}eb`,
              border: '2.5px solid #ffffff',
            }}
          >
            {celda.n}
            {i === CENTRO && globo ? <GloboCentro texto={globo} /> : null}
          </div>
        ))}
      </div>
    </div>
  );
}

function ListaRivales({ c, seleccionado, onElegir }) {
  return (
    <div className="bg-v3-surface rounded-[18px] overflow-hidden">
      <div className="px-4.5 pt-4 pb-3" style={{ padding: '16px 18px 12px' }}>
        <span className="text-sm font-bold tracking-[-.01em] text-v3-ink">{c.listaTitulo}</span>
      </div>

      {/* Nuestra fila, que no es seleccionable: es la referencia. */}
      <div className="flex items-center gap-3 px-4.5 py-3.5 border-t border-black/5 bg-[#fffdf5]" style={{ padding: '13px 18px' }}>
        <span className="w-[22px] text-[11px] font-bold text-v3-golddeep shrink-0">{c.tu}</span>
        <span className="w-[30px] h-[30px] rounded-[9px] bg-v3-gold text-v3-ink text-[12.5px] font-bold flex items-center justify-center shrink-0">
          T
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-semibold tracking-[-.01em] truncate text-v3-ink">
            {c.tuNegocio}
          </span>
          <span className="block text-[11.5px] text-v3-muted mt-px">
            {c.posicionMedia}
            {mediaRejilla(MIA)}
          </span>
        </span>
        <span className="text-right shrink-0">
          <span className="block text-[10.5px] text-v3-faint">{c.puntoAPunto}</span>
          <span className="block text-[13.5px] font-bold text-v3-faint mt-px">—</span>
        </span>
      </div>

      {RIVALES.map((r, i) => {
        const b = balanceCon(r.rejilla);
        const pctGano = Math.round((b.gano / b.total) * 100);
        const pctPierdo = Math.round((b.pierdo / b.total) * 100);
        const gano = b.gano > b.pierdo;
        const pierdo = b.pierdo > b.gano;
        const texto = gano
          ? c.leGanas.replace('{pct}', pctGano)
          : pierdo
            ? c.teGana.replace('{pct}', pctPierdo)
            : c.empatais;
        const sel = i === seleccionado;

        return (
          <button
            key={r.clave}
            type="button"
            aria-pressed={sel}
            onClick={() => onElegir(i)}
            className={`w-full text-left flex items-center gap-3 border-t border-black/5 transition-colors ${
              sel ? 'bg-[#fdf6e7]' : 'hover:bg-white'
            }`}
            style={{ padding: '13px 18px' }}
          >
            <span className="w-[22px] flex items-center justify-center shrink-0">
              <span
                className="w-[18px] h-[18px] rounded-full box-border bg-white flex items-center justify-center"
                style={{ border: `2px solid ${sel ? '#e69a1e' : '#d4d4d4'}` }}
              >
                <span
                  className="w-[7px] h-[7px] rounded-full block"
                  style={{ background: sel ? '#e69a1e' : 'transparent' }}
                />
              </span>
            </span>
            <span
              className="w-[30px] h-[30px] rounded-[9px] text-[12.5px] font-bold flex items-center justify-center shrink-0"
              style={{ background: r.avBg, color: r.avFg }}
            >
              {r.inicial}
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-sm font-semibold tracking-[-.01em] truncate text-v3-ink">
                {c.competidor} {i + 1}
              </span>
              <span className="block text-[11.5px] text-v3-muted mt-px">
                {c.posicionMedia}
                {mediaRejilla(r.rejilla)}
              </span>
            </span>
            <span className="text-right shrink-0">
              <span className="block text-[10.5px] text-v3-faint">{c.puntoAPunto}</span>
              <span
                className="block text-[13.5px] font-bold mt-px"
                style={{ color: gano ? '#16a34a' : pierdo ? '#dc2626' : '#737373' }}
              >
                {texto}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * El modal de dos pasos que explica la cuadrícula.
 *
 * `paso` vale 0 (cerrado), 1 o 2. Se cierra con Escape y pulsando el fondo,
 * que en el diseño no hacía ninguna de las dos cosas.
 *
 * La imagen del paso 1 es opcional a propósito: es una captura de producto que
 * se va a sustituir, y hasta entonces el paso funciona con su texto. Un `<img>`
 * roto sería peor que no tener imagen.
 */
const IMG_PASO1 = '/landing-images/v3/mapa-seo-ejemplo.png';

function ModalGuia({ paso, onPaso, c }) {
  useEffect(() => {
    if (!paso) return undefined;
    const alPulsar = (e) => {
      if (e.key === 'Escape') onPaso(0);
    };
    document.addEventListener('keydown', alPulsar);
    return () => document.removeEventListener('keydown', alPulsar);
  }, [paso, onPaso]);

  if (!paso) return null;

  const esPaso2 = paso === 2;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-6 bg-black/55"
      onClick={() => onPaso(0)}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="v3-guia-titulo"
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-[20px] shadow-[0_30px_70px_rgba(0,0,0,.3)] max-w-[620px] w-full max-h-[86vh] overflow-y-auto p-7"
      >
        <div className="flex items-start gap-4">
          <h3
            id="v3-guia-titulo"
            className="text-[21px] font-bold tracking-[-.02em] leading-[1.25] flex-1 text-v3-ink"
          >
            {esPaso2 ? c.paso2Titulo : c.paso1Titulo}
          </h3>
          <button
            type="button"
            onClick={() => onPaso(0)}
            aria-label={c.cerrar}
            className="text-v3-muted hover:text-v3-ink shrink-0 p-1 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {esPaso2 ? <GuiaPaso2 c={c} /> : <GuiaPaso1 c={c} />}

        <div className="flex gap-2 mt-[22px]" aria-hidden="true">
          {[1, 2].map((n) => (
            <span
              key={n}
              className="flex-1 h-1 rounded-full transition-colors duration-200"
              style={{ background: paso >= n ? '#feae2c' : '#ececea' }}
            />
          ))}
        </div>

        <div className="flex items-center justify-between gap-3 mt-[18px] flex-wrap">
          <span className="text-[13px] text-v3-muted">{c.paso.replace('{n}', paso)}</span>
          <span className="flex gap-2.5">
            {esPaso2 ? (
              <button
                type="button"
                onClick={() => onPaso(1)}
                className="border border-v3-line bg-white text-v3-ink text-sm font-semibold rounded-full px-[18px] py-2.5 whitespace-nowrap hover:bg-v3-surface transition-colors"
              >
                {c.atras}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => onPaso(esPaso2 ? 0 : 2)}
              className="bg-v3-gold hover:bg-v3-golddeep text-v3-ink text-sm font-semibold rounded-full px-5 py-2.5 whitespace-nowrap transition-colors"
            >
              {esPaso2 ? c.entendido : c.siguiente}
            </button>
          </span>
        </div>
      </div>
    </div>
  );
}

function GuiaPaso1({ c }) {
  const [imagenRota, setImagenRota] = useState(false);

  return (
    <>
      <p className="text-[15px] leading-[1.65] text-v3-body mt-3.5">
        {c.paso1Cuerpo} <strong className="text-v3-ink">{c.paso1Destacado}</strong>
      </p>

      {imagenRota ? null : (
        <>
          <div className="border border-v3-line rounded-2xl overflow-hidden mt-[18px]">
            <img
              src={IMG_PASO1}
              alt={c.paso1Alt}
              onError={() => setImagenRota(true)}
              className="w-full h-auto block"
            />
          </div>
          <p className="text-[13px] text-v3-muted mt-2.5">{c.paso1Pie}</p>
        </>
      )}
    </>
  );
}

function GuiaPaso2({ c }) {
  return (
    <>
      <p className="text-[15px] leading-[1.65] text-v3-body mt-3.5">{c.paso2Cuerpo}</p>

      <div className="grid gap-2.5 mt-[18px] grid-cols-[repeat(auto-fit,minmax(130px,1fr))]">
        {c.leyenda.map((texto, i) => (
          <div
            key={texto}
            className="flex items-center gap-2.5 border border-v3-line rounded-xl px-3.5 py-3 text-[13.5px] font-medium text-v3-ink"
          >
            <span
              className="w-3.5 h-3.5 rounded-full block shrink-0"
              style={{ background: COLORES_LEYENDA[i] }}
              aria-hidden="true"
            />
            {texto}
          </div>
        ))}
      </div>

      <div className="bg-v3-greensoft rounded-xl px-4 py-3.5 mt-4 text-[13.5px] leading-[1.6] text-v3-body">
        <strong className="text-v3-ink">{c.paso2Nota1A}</strong>
        {c.paso2Nota1B}
      </div>
      <div className="bg-v3-surface rounded-xl px-4 py-3.5 mt-2.5 text-[13.5px] leading-[1.6] text-v3-body">
        <strong className="text-v3-ink">{c.paso2Nota2A}</strong>
        {c.paso2Nota2B}
      </div>
    </>
  );
}
